import { useCallback, useEffect, useState } from "react";

// Sidebar rail preference: whether the desktop shell shows the full 15.5rem
// sidebar or the 4.25rem icon-only rail. Mirrors ./theme.ts exactly — same
// shape, same failure handling, same pre-paint partner script in index.html
// (without it a collapsed-rail user sees a full-width sidebar flash on every
// load, because the width is decided before React ever mounts).
//
// The flag lives on <html data-rail="collapsed">, not on React state alone, for
// that reason: the inline script can only reach the document element, and CSS
// keyed off :root is what makes the first paint already narrow. useRail() keeps
// the attribute and the stored value in sync once React is running.
//
// Only the collapsed state is stored. No entry → expanded, which is the default
// the shell has always had, so a cleared storage never surprises anyone.

export const RAIL_STORAGE_KEY = "verso-rail";

/** The attribute value the CSS matches (`:root[data-rail="collapsed"]`). */
const COLLAPSED = "collapsed";

export function readStoredRail(): boolean {
  try {
    return localStorage.getItem(RAIL_STORAGE_KEY) === COLLAPSED;
  } catch {
    /* private mode / storage disabled — fall through to expanded */
  }
  return false;
}

export function applyRail(collapsed: boolean): void {
  const root = document.documentElement;
  if (collapsed) root.setAttribute("data-rail", COLLAPSED);
  else root.removeAttribute("data-rail");
}

export function persistRail(collapsed: boolean): void {
  try {
    if (collapsed) localStorage.setItem(RAIL_STORAGE_KEY, COLLAPSED);
    else localStorage.removeItem(RAIL_STORAGE_KEY);
  } catch {
    /* ignore persistence failures */
  }
}

/** Hook: is the rail collapsed + a setter that applies and persists in one call. */
export function useRail(): [boolean, (collapsed: boolean) => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => readStoredRail());

  // Reflect the stored value on mount (in case it changed between the inline
  // script running and React hydrating) — same reason theme.ts does it.
  useEffect(() => {
    applyRail(collapsed);
  }, [collapsed]);

  const set = useCallback((next: boolean) => {
    persistRail(next);
    applyRail(next);
    setCollapsed(next);
  }, []);

  return [collapsed, set];
}
